import {
  FunctionComponent,
  ReactElement,
  useState,
  memo,
  useLayoutEffect,
  useEffect,
  useCallback,
  RefObject,
  Dispatch,
  SetStateAction,
  useRef,
} from 'react';
import debounce from 'lodash/debounce';
import { useUserInteractionTrack } from '../../hooks/tracking/useUserInteractionTrack';

const useClientLayoutEffect = typeof window === 'undefined' ? () => {} : useLayoutEffect;

type SlideableChildrenProps = {
  index: number;
  leftProps: { disabled: boolean; onClick: () => void };
  rightProps: { disabled: boolean; onClick: () => void };
  next?: () => void;
  previous?: () => void;
};

type SlideableProps = {
  itemCount: number;
  itemsPerPage: number;
  children: (props: SlideableChildrenProps) => ReactElement<any>;
  gridRef?: RefObject<HTMLDivElement | null>;
  startIndex?: number;
  trackingLabel?: string;
};

const calcItems = (
  gridRef: SlideableProps['gridRef'],
  itemsPerPage: SlideableProps['itemsPerPage'],
  setSize: Dispatch<
    SetStateAction<{
      minItemsPerPage: number;
      actualItemsPerPage: number;
      size: number;
    } | null>
  >
) => {
  const firstChild = gridRef?.current?.childNodes?.[0] as Element | undefined;
  if (!firstChild) return;

  if (!gridRef?.current) return;

  const boundrect = gridRef?.current?.getBoundingClientRect();
  const rect = firstChild?.getBoundingClientRect();
  if (!boundrect) return;

  const gridRefNodeStyle = window.getComputedStyle(gridRef?.current);
  const firstChildNodeStyle = window.getComputedStyle(firstChild);

  const gap = Number(
    (firstChildNodeStyle.getPropertyValue('margin-right') ?? '0').replace('px', '')
  );
  const paddingLeft = Number(
    (gridRefNodeStyle.getPropertyValue('padding-left') ?? '0').replace('px', '')
  );
  const paddingRight = Number(
    (gridRefNodeStyle.getPropertyValue('padding-right') ?? '0').replace('px', '')
  );

  const actualItemsPerPage = !firstChild
    ? itemsPerPage
    : Math.floor((boundrect.width - paddingLeft - paddingRight + gap) / rect.width || 1);
  const minItemsPerPage = actualItemsPerPage > 1 ? actualItemsPerPage : 1;

  const size = (gridRef?.current?.firstChild as Element).getBoundingClientRect().width;

  setSize({ minItemsPerPage, actualItemsPerPage, size });
};

const Slideable: FunctionComponent<SlideableProps> = ({
  itemCount,
  itemsPerPage,
  children,
  startIndex = 0,
  gridRef,
  trackingLabel,
}) => {
  const loaded = useRef(false);
  const initialScroll = useRef(true);
  const [index, setIndex] = useState(startIndex);
  const [size, setSize] = useState<{
    minItemsPerPage: number;
    actualItemsPerPage: number;
    size: number;
  } | null>(null);
  const trackUserInteraction = useUserInteractionTrack();

  const getNextIndex = useCallback(
    (distance: number, startingIndex: number) => {
      if (!size) return 0;
      const { minItemsPerPage, actualItemsPerPage } = size;
      const nextIndex = Math.max(startingIndex + distance * Math.floor(minItemsPerPage), 0);
      if (nextIndex + Math.floor(actualItemsPerPage) > itemCount) {
        return itemCount - Math.floor(actualItemsPerPage);
      }
      return nextIndex;
    },
    [itemCount, size]
  );

  const slide = useCallback(
    (distance: number) => {
      setIndex(getNextIndex(distance, index));
      if (trackingLabel) {
        trackUserInteraction({ data: { label: `${trackingLabel}-arrow-clicked` } });
      }
    },
    [index, getNextIndex, trackUserInteraction, trackingLabel]
  );

  const next = () => slide(1);

  const previous = useCallback(() => slide(-1), [slide]);

  useEffect(() => {
    const debouncedHandleResize = debounce(() => {
      if (itemCount > itemsPerPage) {
        previous();
        calcItems(gridRef, itemsPerPage, setSize);
      }
    }, 500);
    window.addEventListener('resize', debouncedHandleResize);

    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, [gridRef, itemCount, itemsPerPage, previous]);

  useEffect(() => {
    calcItems(gridRef, itemsPerPage, setSize);
  }, [gridRef, itemsPerPage]);

  useClientLayoutEffect(() => {
    if (!gridRef?.current?.childNodes?.[0] || !size) {
      return;
    }
    const { actualItemsPerPage, size: _size } = size;
    // sets the index to the first item on the last page
    if (loaded.current === false && index !== 0 && index >= itemCount - actualItemsPerPage) {
      const newIndex = itemCount - actualItemsPerPage;
      setIndex(newIndex);
    } else if (itemCount <= actualItemsPerPage) {
      setIndex(0);
    } else {
      const translateX = -index * _size;
      gridRef.current.children[index]?.getBoundingClientRect();
      gridRef.current.style.transition = initialScroll.current ? 'none' : '';
      gridRef.current.style.transform = `translate(${translateX}px, 0)`;
      initialScroll.current = false;
    }
    loaded.current = true;
  }, [gridRef, itemCount, index, size]);

  const leftProps = { disabled: index <= 0, onClick: previous };
  const rightProps = {
    disabled: index + Math.floor(size?.minItemsPerPage ?? 0) >= itemCount,
    onClick: next,
  };
  return children({ index, next, previous, leftProps, rightProps });
};

export default memo(Slideable);
